import { type CalendarOptions, GoogleCalendar, ICalendar, OutlookCalendar } from 'datebook'

import { getMeetingLink } from '@/features/meetings/utils'
import type { GetAllDeliverablesResponseType, V1SessionType, V2SessionType, DeliverablesNotationPossibleStatusType } from '@/features/sessions/types'
import { SessionStatusEnumType } from '@/features/sessions/types'
import dayjsClient from '@/lib/dayjs'
import type { I18nTType, TranslationKeysType } from '@/lib/i18n'

type ColorVariant = 'info' | 'black' | 'success' | 'warning' | 'danger'

export const sessionStatusClassNamesMap = {
  PLANNED: 'planned', // user accepted the session
  FINISHED: 'finished', // user finished the session
  WAIT_CONFIRMATION: 'wait-confirmation', // user has not accepted the session yet
  WAIT_REVIEWS: 'wait-review', // user needs to complete the session
  CANCELLED: 'cancelled', // user declined the session
} as Record<SessionStatusEnumType, string>

export enum SessionOptionEnumDefaultAllType {
  ALL = 'ALL',
}
export type SessionsStatusesOptionsType = SessionStatusEnumType | SessionOptionEnumDefaultAllType

export const sessionStatusesOptionsMap = {
  ALL: 'session.status.all',
  PLANNED: 'session.status.planned',
  WAIT_CONFIRMATION: 'session.status.waitConfirmation',
  WAIT_REVIEWS: 'session.status.waitReviews',
  FINISHED: 'session.status.finished',
  CANCELLED: 'session.status.cancelled',
  NONE: 'session.status.none',
  ONGOING: 'session.status.ongoing',
} as Record<SessionsStatusesOptionsType, TranslationKeysType>

export const sessionAdminStatusesOptionsMap = {
  ALL: 'session.status.admin.all',
  PLANNED: 'session.status.admin.planned',
  ONGOING: 'session.status.admin.ongoing',
  FINISHED: 'session.status.admin.finished',
  CANCELLED: 'session.status.admin.cancelled',
} as Record<SessionsStatusesOptionsType, TranslationKeysType>

export function getPlannedSessions(session: V2SessionType) {
  return session.status === SessionStatusEnumType.PLANNED
}

export function getWaitConfirmationSessions(session: V2SessionType) {
  return session.status === SessionStatusEnumType.WAIT_CONFIRMATION
}

export function getWaitReviewSessions(session: V2SessionType) {
  return session.status === SessionStatusEnumType.WAIT_REVIEW
}

export function getCompletedSessions(session: V2SessionType) {
  return session.status === SessionStatusEnumType.FINISHED
}

export function getCanceledSessions(session: V2SessionType) {
  return session.status === SessionStatusEnumType.CANCELLED
}

export function getUncompletedSessions(session: V2SessionType) {
  return (
    session.status === SessionStatusEnumType.WAIT_CONFIRMATION || session.status === SessionStatusEnumType.WAIT_REVIEW
  )
}

export function getAdminPlannedSessions(session: V1SessionType) {
  return session.statuses.some(
    (status) =>
      status.status === SessionStatusEnumType.PLANNED || status.status === SessionStatusEnumType.WAIT_CONFIRMATION,
  )
}

export function getAdminCompletedSessions(session: V1SessionType) {
  const sessionFinishedDate = dayjsClient(session.from).add(session.duration, 'minutes')

  return (
    session.statuses.every((status) => status.status === SessionStatusEnumType.FINISHED) ||
    sessionFinishedDate.isBefore(dayjsClient())
  )
}

export function getAdminCanceledSessions(session: V1SessionType) {
  return session.statuses.every((status) => status.status === SessionStatusEnumType.CANCELLED)
}

export function setAdminSessionStatus(session: V1SessionType) {
  let sessionStatus: SessionStatusEnumType
  // gathering the planned session first
  if (getAdminPlannedSessions(session)) {
    sessionStatus = SessionStatusEnumType.PLANNED
    // then the canceled session
  } else if (getAdminCanceledSessions(session)) {
    sessionStatus = SessionStatusEnumType.CANCELLED
    // then the finished session
  } else if (getAdminCompletedSessions(session)) {
    sessionStatus = SessionStatusEnumType.FINISHED
    // all other cases are ongoing
  } else {
    sessionStatus = SessionStatusEnumType.ONGOING
  }

  return sessionStatus
}

export function sortSessionsByDate(sessionA: V2SessionType, sessionB: V2SessionType, order: 'asc' | 'desc' = 'asc') {
  if (order === 'asc') {
    return new Date(sessionA.from).getTime() - new Date(sessionB.from).getTime()
  }
  return new Date(sessionB.from).getTime() - new Date(sessionA.from).getTime()
}

// return deliverable by user id
export function returnDeliverableByUserId({
  menteeId,
  deliverables,
}: {
  menteeId: string
  deliverables: GetAllDeliverablesResponseType | undefined
}) {
  return (deliverables ?? []).find((deliverable) => {
    return deliverable.userId === menteeId
  })
}

export async function generateAddToCalendarOptions(session: V2SessionType, t: I18nTType) {
  const event: CalendarOptions = {
    start: new Date(session.from),
    end: new Date(session.to),
    title: `${session.name ? session.name : t('session.addToCalendar.event.title')}`,
    location: session.location === 'visio' ? `${getMeetingLink(session)}` : session.location,
  }

  const icalendar = new ICalendar(event)
  const googleCalendar = new GoogleCalendar(event)
  const outlookCalendar = new OutlookCalendar(event)
  outlookCalendar.setHost('office')
  const ics = icalendar.render()
  const googleCalendarUrl = googleCalendar.render()
  const outlookCalendarUrl = outlookCalendar.render()

  const formattedDate = dayjsClient(new Date(session.from)).format('DD/MM/YYYY HH:mm')

  const filename = `${formattedDate}.ics`
  const file: Blob = await new Promise((resolve) => {
    resolve(new File([ics], filename, { type: 'text/calendar' }))
  })
  const icsUrl = URL.createObjectURL(file)

  return { icsUrl, googleCalendarUrl, outlookCalendarUrl }
}

// Badge utilities - mapping session status to colors and texts
export const sessionStatusBadgeColorsMap = {
  PLANNED: 'info',
  FINISHED: 'success',
  WAIT_CONFIRMATION: 'warning',
  WAIT_REVIEWS: 'warning',
  CANCELLED: 'black',
  ONGOING: 'warning',
  NONE: 'black',
} as Record<SessionStatusEnumType, ColorVariant>

export const sessionStatusBadgeTextMap = {
  PLANNED: 'session.status.planned',
  FINISHED: 'session.status.finished',
  WAIT_CONFIRMATION: 'session.status.waitConfirmation',
  WAIT_REVIEWS: 'session.status.waitReviews',
  CANCELLED: 'session.status.cancelled',
  ONGOING: 'session.status.ongoing',
  NONE: 'session.status.none',
} as Record<SessionStatusEnumType, TranslationKeysType>

export const userSessionStatusBadgeColorsMap = {
  PLANNED: 'success',
  FINISHED: 'success',
  WAIT_CONFIRMATION: 'warning',
  WAIT_REVIEWS: 'warning',
  CANCELLED: 'danger',
} as Record<SessionStatusEnumType, ColorVariant>

export const userSessionStatusBadgeTextMap = {
  PLANNED: 'session.status.user.planned',
  FINISHED: 'session.status.user.present',
  WAIT_CONFIRMATION: 'session.status.user.waitConfirmation',
  WAIT_REVIEWS: 'session.status.user.waitReviews',
  CANCELLED: 'session.status.user.cancelled',
} as Record<SessionStatusEnumType, TranslationKeysType>

export const notationStatusBadgeColorsMap = {
  RATED: 'success',
  NOT_RATED: 'warning',
} as Record<DeliverablesNotationPossibleStatusType, ColorVariant>

export const notationStatusBadgeTextMap = {
  RATED: 'session.status.grade.rated',
  NOT_RATED: 'session.status.grade.toFill',
} as Record<DeliverablesNotationPossibleStatusType, TranslationKeysType>

// Helper functions to get badge props
export function getSessionStatusBadgeProps(status: SessionStatusEnumType) {
  return {
    variant: sessionStatusBadgeColorsMap[status],
    textKey: sessionStatusBadgeTextMap[status],
  }
}

export function getUserSessionStatusBadgeProps(status: SessionStatusEnumType) {
  return {
    variant: userSessionStatusBadgeColorsMap[status],
    textKey: userSessionStatusBadgeTextMap[status],
  }
}

export function getNotationStatusBadgeProps(status: DeliverablesNotationPossibleStatusType) {
  return {
    variant: notationStatusBadgeColorsMap[status],
    textKey: notationStatusBadgeTextMap[status],
  }
}
